import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MdOutlineSettings } from "react-icons/md";
import { FiLoader } from "react-icons/fi";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import {
  getMyAgentSettings,
  saveMyAgentSettings,
} from "../../api/agentSettingsApi";
import {
  COMPANION_APP_LABEL,
  DEFAULT_INSERT_METHOD,
  DEFAULT_PASTE_METHOD,
  getAgentDownloadUrl,
} from "../../agent/constants";
import { parseWindowTitleList } from "../../utils/agentResponses";
import { NAVBAR_ICON_BUTTON_ICON_CLASS } from "../navbar/navbarIconButtonClasses";

const PANEL_WIDTH = 320;
const PANEL_HEIGHT_EST = 360;
const GAP = 8;

function computePanelPosition(buttonEl, heightEst = PANEL_HEIGHT_EST) {
  if (typeof window === "undefined" || !buttonEl) return null;
  const rect = buttonEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(PANEL_WIDTH, vw - 20);

  let left = rect.right - width;
  left = Math.max(10, Math.min(left, vw - width - 10));

  let top = rect.bottom + GAP;
  if (top + heightEst > vh - 10) {
    top = Math.max(10, rect.top - heightEst - GAP);
  }

  return { top, left, width };
}

/**
 * Target-window settings for the Windows companion app (CRE only).
 */
export default function CompanionAgentSettingsPopover({
  connected,
  sendCommand,
  isRTL = false,
  buttonClassName,
  onBeforeOpen,
}) {
  const { t } = useTranslation("common");
  const { isCreAuth } = usePatientAuthFlags();
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const creUserEmail = useGlobalStore((s) => s.creUser?.email ?? "");

  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ text: "", tone: "" });

  const [configured, setConfigured] = useState(false);
  const [windowsUser, setWindowsUser] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [availableWindows, setAvailableWindows] = useState([]);
  const [selectedWindows, setSelectedWindows] = useState([]);
  const [userOrganisations, setUserOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState("");

  const panelId = useId();

  const updatePos = useCallback(() => {
    if (!open || !btnRef.current) return;
    setPos(computePanelPosition(btnRef.current));
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (event) => {
      const target = event.target;
      if (
        panelRef.current?.contains(target) ||
        btnRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open, updatePos]);

  const refreshAgentContext = useCallback(async () => {
    const token = await useGlobalStore.getState().getAccessToken();
    const identity = await sendCommand({ action: "get_identity" }, token);
    if (!identity) {
      setWindowsUser("");
      setAvailableWindows([]);
      setStatus({
        text: t("companionAgent.notConnected", { app: COMPANION_APP_LABEL }),
        tone: "error",
      });
      return false;
    }

    setWindowsUser(
      identity.success ? String(identity.data?.username ?? "") : "",
    );

    const winResp = await sendCommand({ action: "list_windows" }, token);
    if (!winResp) {
      setAvailableWindows([]);
      setStatus({
        text: t("companionAgent.notConnected", { app: COMPANION_APP_LABEL }),
        tone: "error",
      });
      return false;
    }

    const windows =
      winResp.success && Array.isArray(winResp.data?.windows)
        ? winResp.data.windows.filter(Boolean)
        : [];
    setAvailableWindows(windows);
    return true;
  }, [sendCommand, t]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setStatus({ text: "", tone: "" });
    try {
      await refreshAgentContext();

      const res = await getMyAgentSettings();
      const payload = res?.payload ?? null;
      setConfigured(Boolean(payload?.windowTitle));
      // Always use Validate email — GetMySettings userEmail can be Entra username.
      setUserEmail(creUserEmail || "");
      setSelectedWindows(parseWindowTitleList(payload?.windowTitle));

      const orgs = Array.isArray(res?.userOrganisations)
        ? res.userOrganisations.filter((o) => o?.organisationId)
        : [];
      setUserOrganisations(orgs);

      const savedOrgId = payload?.organisationId ?? "";
      const defaultOrgId =
        savedOrgId ||
        (isCreAuth ? creSelectedOrganisationId : "") ||
        orgs.find((o) => o.isDefault)?.organisationId ||
        orgs[0]?.organisationId ||
        "";
      setOrganisationId(defaultOrgId);
    } catch (error) {
      setStatus({
        text:
          error instanceof Error
            ? error.message
            : t("companionAgent.loadFailed"),
        tone: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    creSelectedOrganisationId,
    creUserEmail,
    isCreAuth,
    refreshAgentContext,
    t,
  ]);

  useEffect(() => {
    if (!open) return;
    void loadSettings();
  }, [open, loadSettings]);

  const toggleWindow = (title) => {
    setSelectedWindows((prev) =>
      prev.includes(title)
        ? prev.filter((w) => w !== title)
        : [...prev, title],
    );
  };

  const handleSave = async () => {
    if (typeof onBeforeOpen === "function" && !onBeforeOpen()) {
      return;
    }
    if (selectedWindows.length === 0) {
      setStatus({ text: t("companionAgent.addWindow"), tone: "error" });
      return;
    }

    setSaving(true);
    setStatus({ text: t("companionAgent.saving"), tone: "" });
    try {
      const res = await saveMyAgentSettings({
        windowsUsername: windowsUser,
        organisationId: organisationId || "",
        windowTitle: selectedWindows.join("|"),
        insertMethod: DEFAULT_INSERT_METHOD,
        pasteMethod: DEFAULT_PASTE_METHOD,
      });

      if (!res?.success) {
        throw new Error(res?.error ?? t("companionAgent.saveFailed"));
      }

      const payload = res.payload;
      setConfigured(Boolean(payload?.windowTitle));
      setStatus({ text: t("companionAgent.saved"), tone: "ok" });
    } catch (error) {
      setStatus({
        text:
          error instanceof Error
            ? error.message
            : t("companionAgent.saveFailed"),
        tone: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const statusClass =
    status.tone === "error"
      ? "text-red-600 dark:text-red-400"
      : status.tone === "ok"
        ? "text-green-700 dark:text-green-400"
        : "text-gray-600 dark:text-gray-300";

  const settingsLabel = `${t("companionAgent.settingsTitle")} — ${
    connected
      ? t("companionAgent.connected")
      : t("companionAgent.disconnected")
  }`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={buttonClassName}
        title={settingsLabel}
        aria-label={settingsLabel}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            if (typeof onBeforeOpen === "function" && !onBeforeOpen()) {
              return false;
            }
            return true;
          });
        }}
      >
        <MdOutlineSettings className={NAVBAR_ICON_BUTTON_ICON_CLASS} />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={t("companionAgent.settingsTitle")}
            dir={isRTL ? "rtl" : "ltr"}
            className="fixed z-[10050] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl p-4 text-sm"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {t("companionAgent.settingsTitle")}
              </h3>
              <span
                className={`text-xs font-medium ${
                  connected
                    ? "text-green-700 dark:text-green-400"
                    : "text-amber-700 dark:text-amber-400"
                }`}
              >
                {connected
                  ? t("companionAgent.connected")
                  : t("companionAgent.disconnected")}
              </span>
            </div>

            {!connected && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                {t("companionAgent.disconnectedHint", {
                  app: COMPANION_APP_LABEL,
                })}{" "}
                <a
                  href={getAgentDownloadUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5a7a12] dark:text-[#CBEA7B] underline"
                >
                  {t("companionAgent.downloadCompanion")}
                </a>
              </p>
            )}

            {configured ? (
              <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                {t("companionAgent.configuredNotice")}
              </p>
            ) : (
              <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                {userEmail
                  ? t("companionAgent.configureFor", { email: userEmail })
                  : t("companionAgent.configureBelow")}
              </p>
            )}

            {loading ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 py-6 justify-center">
                <FiLoader className="animate-spin" />
                {t("loading")}
              </div>
            ) : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {userEmail && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t("companionAgent.accountEmail")}
                    </div>
                    <div className="text-gray-900 dark:text-gray-100 break-all">
                      {userEmail}
                    </div>
                  </div>
                )}

                {windowsUser && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t("companionAgent.windowsUser")}
                    </div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {windowsUser}
                    </div>
                  </div>
                )}

                {userOrganisations.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      {t("companionAgent.organisation")}
                    </label>
                    <select
                      value={organisationId}
                      onChange={(e) => setOrganisationId(e.target.value)}
                      disabled={saving || userOrganisations.length <= 1}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-gray-900 dark:text-gray-100"
                    >
                      <option value="" disabled hidden>
                        {t("companionAgent.selectOrganisation")}
                      </option>
                      {userOrganisations.map((org) => (
                        <option
                          key={org.organisationId}
                          value={org.organisationId}
                        >
                          {org.name || org.domain || org.organisationId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t("companionAgent.targetWindows")}
                    </label>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void refreshAgentContext()}
                      className="text-xs text-[#5a7a12] dark:text-[#CBEA7B] hover:underline disabled:opacity-50"
                    >
                      {t("companionAgent.refreshWindows")}
                    </button>
                  </div>
                  {availableWindows.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("companionAgent.noWindows")}
                    </p>
                  ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2">
                      {availableWindows.map((title) => (
                        <li key={title}>
                          <label className="flex items-start gap-2 text-xs text-gray-800 dark:text-gray-100 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedWindows.includes(title)}
                              onChange={() => toggleWindow(title)}
                              disabled={saving}
                              className="mt-0.5"
                            />
                            <span className="break-all">{title}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {status.text && (
              <p className={`mt-3 text-xs ${statusClass}`}>{status.text}</p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                disabled={loading || saving}
                onClick={() => void handleSave()}
                className="px-3 py-1.5 text-xs rounded-md bg-[#A4C639] hover:bg-[#A4C639]/90 text-gray-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {saving && <FiLoader className="animate-spin w-3.5 h-3.5" />}
                {t("companionAgent.save")}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
